// ============================================
// Shell do app: topbar com marca + menu do usuário; conteúdo via <Outlet/>.
// (As abas de seção ficam no RaizLayout, portado do módulo.)
// ============================================

import { useEffect, useRef, useState } from 'react'
import { Outlet, Link as RouterLink, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import AppBar from '@mui/material/AppBar'
import Avatar from '@mui/material/Avatar'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import ListSubheader from '@mui/material/ListSubheader'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded'
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded'
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded'
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded'
import TvRoundedIcon from '@mui/icons-material/TvRounded'
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useAuth } from '../auth/AuthContext'
import { useColorMode } from '../theme/ColorModeContext'
import { raizService } from '../services/raizService'
import { useRaizStream } from '../hooks/useRaizStream'
import { useToast } from './ToastProvider'
import { RaizLogo } from './RaizLogo'
import CommandPalette from './CommandPalette'
import PushNotificationsDialog from './PushNotificationsDialog'
import { pendingExcursionCount } from '../pages/Raiz/raizFormat'

const COMPANY_ROLE_LABEL = { company_admin: 'Administrador', operator: 'Operador', auditor: 'Auditor' } as const

export default function AppShell() {
  const { user, logout, isAdmin, switchCompany } = useAuth()
  const { mode, toggle } = useColorMode()
  const { showToast } = useToast()
  const navigate = useNavigate()
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const [switching, setSwitching] = useState(false)
  // Web Push (#61 fase 2): opt-in por dispositivo, aberto pelo menu da conta.
  const [pushOpen, setPushOpen] = useState(false)

  // Stream ao vivo (SSE): montado UMA vez aqui. Patch instantâneo do cache +
  // toast de nova excursão; o polling de 30s das páginas segue de fallback.
  // F4: keyed pela empresa ativa — trocar de empresa fecha e reabre o
  // EventSource (o servidor fixa a empresa no connect).
  useRaizStream({ enabled: user != null, companyId: user?.companyId ?? null })

  // F4 — QUALQUER mudança de empresa ativa (troca neste menu OU adoção cross-
  // aba via 409 company_mismatch) volta para o início e anuncia: rotas com ids
  // da empresa anterior (/:mac, unidade selecionada) ficariam órfãs em erro.
  // Efeito único para os dois caminhos — o handler do menu só chama a troca.
  const prevCompanyRef = useRef(user?.companyId ?? null)
  useEffect(() => {
    const prev = prevCompanyRef.current
    const curr = user?.companyId ?? null
    prevCompanyRef.current = curr
    if (prev != null && curr != null && prev !== curr) {
      navigate('/')
      const nome = user?.memberships?.find((m) => m.companyId === curr)?.companyName
      showToast(`Empresa ativa: ${nome ?? 'nova empresa'}`, { severity: 'success' })
    }
    // showToast/navigate são estáveis (provider/router) — a dep relevante é o user.
  }, [user, navigate, showToast])

  const onSwitchCompany = (companyId: string): void => {
    if (switching || companyId === user?.companyId) { setAnchor(null); return }
    setSwitching(true)
    switchCompany(companyId)
      .then(() => setAnchor(null))
      .catch(() => {
        showToast('Não foi possível trocar de empresa. Tente novamente.', { severity: 'error' })
      })
      .finally(() => setSwitching(false))
  }

  // Sino de excursões abertas — o crítico grita em qualquer tela.
  const excursionsQ = useQuery({
    queryKey: ['raiz', 'excursions', 'summary'],
    queryFn: raizService.getExcursionsSummary,
    refetchInterval: 30_000,
    enabled: user != null,
  })
  const openCount = pendingExcursionCount(excursionsQ.data)
  const bellTitle = openCount === 0
    ? 'Nenhuma excursão aberta'
    : openCount === 1
      ? '1 excursão aberta'
      : `${openCount} excursões abertas`

  const initials = (user?.name || user?.email || '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('')

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Skip-link (WCAG 2.4.1): invisível até receber foco por teclado. */}
      <Box
        component="a"
        href="#conteudo-principal"
        // Com navegação lateral dentro do <main>, pular para o <main> caía na
        // própria navegação: quem tiver moldura de página (#conteudo-pagina)
        // recebe o foco; senão, o <main>.
        onClick={(e) => {
          const target = document.getElementById('conteudo-pagina') ?? document.getElementById('conteudo-principal')
          if (target == null) return
          e.preventDefault()
          target.focus()
        }}
        sx={{
          position: 'absolute',
          left: 12,
          top: 12,
          zIndex: (t) => t.zIndex.appBar + 1,
          bgcolor: 'background.paper',
          color: 'primary.main',
          fontWeight: 700,
          textDecoration: 'none',
          px: 2,
          py: 1,
          borderRadius: 1,
          border: '2px solid',
          borderColor: 'primary.main',
          boxShadow: 4,
          // Fora da tela (mas focável) até o foco chegar nele.
          transform: 'translateY(-200%)',
          '&:focus': { transform: 'translateY(0)' },
        }}
      >
        Pular para o conteúdo
      </Box>
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ gap: { xs: 0.75, sm: 1.5 } }}>
          <Stack
            component={RouterLink}
            to="/"
            direction="row"
            spacing={1.2}
            alignItems="center"
            sx={{ textDecoration: 'none', color: 'inherit', minWidth: 0 }}
          >
            <RaizLogo size={30} />
            <Typography variant="h6" noWrap sx={{ letterSpacing: '-0.01em' }}>
              RAIZ IoT
            </Typography>
          </Stack>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Paleta de comandos (⌘K)">
            <Chip
              label="⌘K"
              size="small"
              variant="outlined"
              onClick={() => window.dispatchEvent(new Event('raiz:open-palette'))}
              aria-label="Abrir paleta de comandos"
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                color: 'inherit',
                borderColor: 'rgba(255,255,255,0.4)',
                fontFamily: 'monospace',
                letterSpacing: '0.02em',
              }}
            />
          </Tooltip>
          <Tooltip title={mode === 'dark' ? 'Tema claro' : 'Tema escuro'}>
            <IconButton onClick={toggle} color="inherit" aria-label={mode === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}>
              {mode === 'dark' ? <LightModeRoundedIcon /> : <DarkModeRoundedIcon />}
            </IconButton>
          </Tooltip>
          {/* No celular o sino sai: o badge de excursões vive na bottom nav. */}
          <Tooltip title={bellTitle}>
            <IconButton
              component={RouterLink}
              to="/events/excursions"
              color="inherit"
              aria-label={bellTitle}
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            >
              <Badge badgeContent={openCount} color="error" overlap="circular">
                <NotificationsRoundedIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          {isAdmin && (
            <Chip
              label="Admin"
              size="small"
              sx={{ color: 'inherit', borderColor: 'rgba(255,255,255,0.4)', display: { xs: 'none', sm: 'inline-flex' } }}
              variant="outlined"
            />
          )}
          <IconButton onClick={(e) => setAnchor(e.currentTarget)} size="small" aria-label="Conta">
            <Avatar sx={{ width: 34, height: 34, bgcolor: 'rgba(255,255,255,0.18)', fontSize: 14, fontWeight: 700 }}>
              {initials}
            </Avatar>
          </IconButton>
          <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2">{user?.name ?? user?.email}</Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
            {/* Minha conta (#131): nome, senha e empresas do PRÓPRIO usuário —
                qualquer papel; o que é dos outros fica em Configurações. */}
            <MenuItem component={RouterLink} to="/account" onClick={() => setAnchor(null)}>
              <ListItemIcon>
                <ManageAccountsRoundedIcon fontSize="small" />
              </ListItemIcon>
              Minha conta
            </MenuItem>
            {/* F4 — switcher: todos os vínculos, papel POR empresa, check na
                ativa. O ?. em memberships segura a janela start-first (API
                velha responde /me sem o campo — menu some, tela não quebra). */}
            {/* Filhos DIRETOS do Menu (array, não <Box>): o MenuList só anda
                pelas setas entre os próprios filhos — embrulhadas, as empresas
                ficavam inalcançáveis pelo teclado. */}
            {(user?.memberships?.length ?? 0) > 0 && [
              <Divider key="empresa-div-top" />,
              <ListSubheader
                key="empresa-label"
                disableSticky
                sx={{ typography: 'overline', color: 'text.secondary', lineHeight: 2, pt: 1, bgcolor: 'transparent' }}
              >
                Empresa
              </ListSubheader>,
              ...(user?.memberships ?? []).map((m) => {
                  const active = m.companyId === user?.companyId
                  return (
                    <MenuItem
                      key={m.companyId}
                      onClick={() => onSwitchCompany(m.companyId)}
                      disabled={switching}
                      selected={active}
                      aria-current={active ? 'true' : undefined}
                    >
                      <ListItemIcon>
                        <ApartmentRoundedIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={m.companyName}
                        secondary={COMPANY_ROLE_LABEL[m.role]}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      {active && <CheckRoundedIcon fontSize="small" color="primary" sx={{ ml: 1.5 }} />}
                    </MenuItem>
                  )
                }),
              <Divider key="empresa-div-bottom" />,
            ]}
            <MenuItem
              onClick={() => {
                setAnchor(null)
                setPushOpen(true)
              }}
            >
              <ListItemIcon>
                <NotificationsActiveRoundedIcon fontSize="small" />
              </ListItemIcon>
              Notificações
            </MenuItem>
            <MenuItem component={RouterLink} to="/tv" onClick={() => setAnchor(null)}>
              <ListItemIcon>
                <TvRoundedIcon fontSize="small" />
              </ListItemIcon>
              Modo TV
            </MenuItem>
            {/* Saiu do menu "Mais": não é lugar de trabalho, é referência. */}
            <MenuItem component={RouterLink} to="/about" onClick={() => setAnchor(null)}>
              <ListItemIcon>
                <InfoOutlinedIcon fontSize="small" />
              </ListItemIcon>
              Sobre o RAIZ
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAnchor(null)
                void logout()
              }}
            >
              <ListItemIcon>
                <LogoutRoundedIcon fontSize="small" />
              </ListItemIcon>
              Sair
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      {/* tabIndex -1: alvo programável do skip-link sem entrar na ordem de tabulação. */}
      <Box component="main" id="conteudo-principal" tabIndex={-1} sx={{ flexGrow: 1, outline: 'none' }}>
        <Outlet />
      </Box>
      <PushNotificationsDialog open={pushOpen} onClose={() => setPushOpen(false)} />
      <CommandPalette />
    </Box>
  )
}
